import { createInventoryItem, resolveItemTypeToMeta } from "game-common/item/item"
import { randomAppearanceFor } from "game-common/lpc/lpc"
import { Coordinate, Entity, Inventory } from "game-common/models"
import { BaseMapBuilder, MapEntry } from "game-common/room/base_map_builder"
import { forestGrassTile } from "game-common/room/ember/grass_art"
import { houseRoofOffset, STOREY_RISE_PX } from "game-common/room/ember/two_storey_house"
import { GridCell, HouseBuilder } from "game-common/room/house/feature/house"
import { blankMap } from "game-common/util"
import { BLEND_MODES, Container, Graphics, Sprite, Texture } from "pixi.js"
import { isMobile } from "../../../../client_util"
import { contentSheetResource } from "../content_textures"
import { BuildCursor, BuildPlacementFeedback } from "../effects/build_placement_feedback"
import { GuiManager } from "../gui_manager"
import { FireSprite } from "../sprites/fire_sprite"
import { LpcComposite } from "../sprites/lpc_composite"
import { stackedLadderPixels } from "../stacked_ladder"
import { glowDisc, sparkBurst, REWARD_GOLD, REWARD_GOLD_HOT } from "./chapter_reward_art"
import { easeInCubic, easeInOutCubic, easeOutCubic, lerp, quadPoint, span } from "./chapter_reward_motion"
import { EquippedWidget, SLOT, SLOT_PITCH, TRAY_HEIGHT, TRAY_PAD } from "./equipped"
import { FlameBackdrop } from "./flame_backdrop"
import { DemoCursor, DemoStamp, drawDioramaFrame, drawRipples, Pt, TutorialDemo } from "./tutorial_demo"

// Chapter five's ladder helper, shown once the ladder is crafted: a pointer picks the ladder off the
// ready bar, the player walks in through the shelter's doorway -- its roof cut away over them as the
// map does indoors -- and the pointer puts the ladder down in the shelter's north-west corner, where it
// rises against the walls up through the roof.
//
// As with the roof helper (building_tutorial.ts), what is inside the frame is the REAL thing: the
// shelter's walls and finished roof are laid out by the camp's own HouseBuilder and raised as the map
// raises every roof; the body is an LPC body in the player's own look; the ready bar is the HUD's
// EquippedWidget; the ladder is the map's own stacked-ladder art, seen from under its roof; and the
// cell outline, the click and the build are the game's build cursor and BuildPlacementFeedback.

// The board, and the diorama inside it, as the roof helper lays them out. CELL must equal TileSize.
const CELL = 32
const COLS = 10, ROWS = 8
const SCENE_W = COLS * CELL, SCENE_H = ROWS * CELL
const WIDTH = SCENE_W + 44
const SCENE_X = (WIDTH - SCENE_W) / 2
const SCENE_Y = 92
const CAPTION_Y = SCENE_Y + SCENE_H + 13
const CAPTION_H = 36
const HEIGHT = CAPTION_Y + CAPTION_H + 10

// The starter shelter (EmberRoomService.ruinedWallCells): a 5×5 wooden perimeter round the hearth, a
// one-tile doorway east of centre on its south side, and the whole 5×5 roofed, as chapter five
// stands on it. The ladder goes in the interior's north-west corner.
const HEARTH = { x: 6, y: 4 }
const SHELTER_RADIUS = 2
const SHELTER_SIDE = SHELTER_RADIUS * 2 + 1
const SHELTER_LEFT = HEARTH.x - SHELTER_RADIUS, SHELTER_TOP = HEARTH.y - SHELTER_RADIUS
const DOOR = { x: HEARTH.x + 1, y: HEARTH.y + SHELTER_RADIUS }
export const LADDER_CELL = { x: HEARTH.x - 1, y: HEARTH.y - 1 }
// Cells the ladder fits in, for its ghost under the pointer: the interior's north row.
const GHOST_CELLS = [-1, 0, 1].map(dx => ({ x: HEARTH.x + dx, y: LADDER_CELL.y }))
const FIRE_X = (HEARTH.x + 0.5) * CELL
const FIRE_BASE_Y = HEARTH.y * CELL + 20
const FLAME_W = 34, FLAME_H = 44
// Firelight is ADDED over grass, so it is starved of green: a gold glow there turns lime.
const FIRELIGHT = 0xff3d08
// Player.buildGhostAlpha: a pending ghost at BUILD_GHOST_ALPHA.
const GHOST_ALPHA = 0.4
// A body is drawn about its HIP; the soles are FEET_BELOW_HIP under it.
const FEET_BELOW_HIP = 28
// World stacking, as the map uses it: a cell's sprites by their row (a wall's top by its wall's), a
// body by its feet, then the roof and the build overlays over all.
const rowZ = (y: number) => 100 + y * CELL + CELL / 3
const bodyZ = (feetY: number) => 100 + feetY
const ROOF_Z = 100000, CURSOR_Z = 100000.1
// The LPC facings (lpc_composite's): toward the camera, west, away.
const FACING_FRONT = Math.PI / 2, FACING_WEST = Math.PI, FACING_BACK = -Math.PI / 2

const cellKey = ({ x, y }: Coordinate) => `${x},${y}`
const squareCells = (side: number, left: number, top: number): Coordinate[] => {
    const cells: Coordinate[] = []
    for (let y = 0; y < side; y++) for (let x = 0; x < side; x++) cells.push({ x: left + x, y: top + y })
    return cells
}
const shelterWalls = (): Coordinate[] => squareCells(SHELTER_SIDE, SHELTER_LEFT, SHELTER_TOP).filter(({ x, y }) =>
    (x === SHELTER_LEFT || x === SHELTER_LEFT + SHELTER_SIDE - 1 || y === SHELTER_TOP || y === SHELTER_TOP + SHELTER_SIDE - 1) &&
    !(x === DOOR.x && y === DOOR.y))
const shelterRoof = (): Coordinate[] => squareCells(SHELTER_SIDE, SHELTER_LEFT, SHELTER_TOP)

/** The map entries the camp's builder lays for the walls and the finished roof (house_roof.ts does the same). */
const shelterEntries = (): MapEntry[] => {
    // The builder reads its grids [y][x]; a square grid keeps blankMap's [x][y] from mattering.
    const grid = () => blankMap<GridCell>(COLS, COLS)
    const house = grid(), roof = grid()
    shelterWalls().forEach(({ x, y }) => { house[y][x] = { itemType: "wall-wood" } })
    shelterRoof().forEach(({ x, y }) => { roof[y][x] = { itemType: "roof-wood" } })
    const builder = new HouseBuilder({} as BaseMapBuilder, {
        blockingTileSpec: { tilePos: 121, tilesetId: "lpc-terrain" },
        house, roof, entity: grid(), floor: grid(), floor2: grid(), decor: grid(),
    })
    builder.build("ladder-tutorial")
    return builder.featureSpec.mapEntries
}
// A map entry's tilePos is 0-based within its tileset; the atlas names that cell frame tilePos + 1.
const atlasFrame = (entry: MapEntry) => entry.tileSpec.tilePos + 1
// A wall's top is laid in the cell above it, parented to the wall: it stacks with its wall.
const entryRow = (entry: MapEntry) => entry.context?.parent?.y ?? entry.y

// ---- The timeline ------------------------------------------------------------------------------
//
// Each step animates, then holds until Next; Back restarts the step before, and Replay the first.
// Everything inside it is a pure function of its clock, so any instant can be posed exactly.

export const LADDER_DEMO_MS = 6600
const SELECT_AT = 900 // the pointer clicks the ladder on the ready bar
const WALK_STEP_AT = 1500
const WALK_FROM = 1700 // off along the wall to the doorway...
const TURN_AT = 2300 // ...then north, in through it
const INSIDE_AT = 3050
const PLACE_STEP_AT = 3400
const PLACE_AT = 4500 // the pointer clicks the corner
const BUILT_AT = PLACE_AT + 140 // a lone tile builds at once (Player.startReleasedBuildGroup)
const STAMP_AT = BUILT_AT + 250
const DONE_AT = BUILT_AT + 450
const REPLAY_AT = 6100
const PRESS_MS = 150
const ROOF_FADE_MS = 350

// Where the feet go: up from the south-east, along the south wall, and in through the doorway.
const START: Pt = { x: 9.4 * CELL, y: 7.55 * CELL }
const AT_DOOR: Pt = { x: (DOOR.x + 0.5) * CELL, y: START.y }
const INSIDE: Pt = { x: (DOOR.x + 0.5) * CELL, y: (DOOR.y - 0.6) * CELL }
// The roof is cut away once the feet are under it: across the doorway's south edge.
const ENTER_AT = lerp(TURN_AT, INSIDE_AT, (AT_DOOR.y - (DOOR.y + 1) * CELL) / (AT_DOOR.y - INSIDE.y))

const caption = (touch: boolean) => [
    touch ? "Tap the ladder on your ready bar." : "Pick the ladder from your ready bar.",
    "Walk into the shelter through its doorway.",
    touch ? "Tap the north-west corner inside to put the ladder there." : "Click the north-west corner inside to put the ladder there.",
]
const DONE_CAPTION = "The ladder is up. Climb it to the roof."

const lerpPt = (from: Pt, to: Pt, k: number): Pt => ({ x: lerp(from.x, to.x, k), y: lerp(from.y, to.y, k) })

/** Where the demonstration is at `elapsedMs` into one play-through. */
export const ladderTutorialPose = (elapsedMs: number) => {
    const t = Math.min(LADDER_DEMO_MS, Math.max(0, elapsedMs))
    // A steady walking pace, so plain lerps: a body does not ease into its stride.
    const feet = t < TURN_AT ? lerpPt(START, AT_DOOR, span(t, WALK_FROM, TURN_AT)) : lerpPt(AT_DOOR, INSIDE, span(t, TURN_AT, INSIDE_AT))
    return {
        step: t < WALK_STEP_AT ? 0 : t < PLACE_STEP_AT ? 1 : 2,
        selected: t < SELECT_AT ? 0 : 1,
        feet,
        moving: t > WALK_FROM && t < INSIDE_AT,
        heading: t < TURN_AT ? { x: -1, y: 0 } : { x: 0, y: -1 },
        facing: t <= WALK_FROM ? FACING_FRONT : t < TURN_AT ? FACING_WEST : FACING_BACK,
        roofAlpha: 1 - easeInOutCubic(span(t, ENTER_AT, ENTER_AT + ROOF_FADE_MS)),
        inside: t >= ENTER_AT,
        placed: t >= BUILT_AT,
        done: t >= DONE_AT,
    }
}

export interface LadderCursorStops { rest: Pt; slot: Pt; enter: Pt; corner: Pt }

// A hand moves in arcs, not along a ruler.
const glide = (from: Pt, to: Pt, k: number) =>
    quadPoint(from, { x: (from.x + to.x) / 2, y: Math.min(from.y, to.y) - 18 }, to, easeInOutCubic(k))

/** The pointer: onto the ladder's slot, out of the way while the player walks, then onto the corner. */
export const ladderCursorPose = (elapsedMs: number, at: LadderCursorStops): DemoCursor => {
    const t = Math.min(LADDER_DEMO_MS, Math.max(0, elapsedMs))
    let p: Pt, alpha: number
    if (t < PLACE_STEP_AT) {
        // Onto the slot, and resting there until the walk, when it drifts off the bar and fades.
        const drift = easeOutCubic(span(t, WALK_STEP_AT, WALK_STEP_AT + 400))
        p = glide(at.rest, at.slot, span(t, 120, SELECT_AT - 100))
        p = { x: p.x + 22 * drift, y: p.y - 28 * drift }
        alpha = Math.min(easeOutCubic(span(t, 0, 250)), 1 - easeInCubic(span(t, WALK_STEP_AT, WALK_STEP_AT + 350)))
    } else {
        p = t < PLACE_AT + 180
            ? glide(at.enter, at.corner, span(t, PLACE_STEP_AT + 150, PLACE_AT - 150))
            : (() => {
                const k = easeInOutCubic(span(t, PLACE_AT + 180, PLACE_AT + 650))
                return { x: at.corner.x + 26 * k, y: at.corner.y + 10 * k }
            })()
        alpha = Math.min(easeOutCubic(span(t, PLACE_STEP_AT, PLACE_STEP_AT + 250)),
            1 - easeInCubic(span(t, PLACE_AT + 250, PLACE_AT + 750)))
    }
    return {
        ...p,
        pressed: (t >= SELECT_AT && t < SELECT_AT + PRESS_MS) || (t >= PLACE_AT && t < PLACE_AT + PRESS_MS),
        alpha,
    }
}

/** The player's own look, or a castaway's when there is no player to borrow it from. */
const walkerBody = (player?: Entity): Entity => {
    const body = new Entity()
    body.entityType = "player"
    body.appearance = player?.appearance || randomAppearanceFor(undefined, "human", "male")
    if (player?.inventory) body.inventory = player.inventory
    body.weapon = { weaponType: "none" }
    body.movement.angle = FACING_FRONT
    return body
}

interface SceneArt { sprite: Sprite; sheet: string; tile: number }

/** Real game art, body, ready bar and build feedback in an isolated scene. Nothing here reaches the game. */
export class LadderTutorial extends TutorialDemo {
    private scene = new Container()
    private frame = new Graphics()
    private fx = new Graphics()
    private hearthGlow = new Graphics()
    private flame: FireSprite | FlameBackdrop
    private stamp = new DemoStamp("LADDER PLACED")
    private readyBar: EquippedWidget
    private feedback: BuildPlacementFeedback
    private target = new BuildCursor(() => this.clock)
    private walker = new LpcComposite("ladder-tutorial")
    private body: Entity
    // The ladder as it stands from under its roof: its flight up to the hatch it climbs through.
    private ladder = new Graphics()
    private inventory: Inventory = { items: ["axe", "stairs"].map((type: any, i) => ({
        ...createInventoryItem(resolveItemTypeToMeta(type), { id: `ladder-tutorial-${type}`, quantity: 1 }),
        readied: true, active: i === 0,
    })), abilities: {}, lastUpdateTs: 0 }
    // Grass, walls and the campfire: textured from their atlases once those have loaded.
    private art: SceneArt[] = []
    private roof: SceneArt[] = []
    // The roof is drawn this far off its cells (negative: up), as the map draws every roof.
    private lift = houseRoofOffset()
    private stops: LadderCursorStops
    private played = { placed: false, built: false }
    private selected = -1

    constructor(hover: (over: boolean) => void, dismiss: () => void, touch = isMobile(), player?: Entity) {
        super({
            width: WIDTH, height: HEIGHT, padX: SCENE_X, contentWidth: SCENE_W,
            captionY: CAPTION_Y, captionH: CAPTION_H, overline: "BUILDING", title: "Put up the ladder",
            steps: {
                labels: ["PICK", "WALK", "PLACE"],
                litAt: [0, WALK_STEP_AT, PLACE_STEP_AT],
                doneAt: [SELECT_AT, INSIDE_AT, BUILT_AT],
                linkFill: [[SELECT_AT + 120, WALK_STEP_AT], [INSIDE_AT, PLACE_STEP_AT]],
            },
            durationMs: LADDER_DEMO_MS, replayAt: REPLAY_AT,
            manualStepStarts: [0, WALK_STEP_AT, PLACE_STEP_AT],
        }, hover, dismiss)
        this.touch = touch
        this.body = walkerBody(player)

        // Sortable, so everything keeps its world stacking.
        this.scene.position.set(SCENE_X, SCENE_Y)
        this.scene.sortableChildren = true
        this.content.addChild(this.scene)
        for (let y = 0; y < ROWS; y++) for (let x = 0; x < COLS; x++) {
            this.addTile(this.art, "forest-grass", forestGrassTile(x, y), x, y * CELL, 0)
        }
        this.hearthGlow.blendMode = BLEND_MODES.ADD
        this.hearthGlow.zIndex = 1
        this.scene.addChild(this.hearthGlow)
        ;[759, 760, 761].forEach((tile, i) => this.addTile(this.art, "lpc-terrain", tile, HEARTH.x - 1 + i, HEARTH.y * CELL, rowZ(HEARTH.y)))
        this.flame = this.buildFlame()
        this.flame.zIndex = rowZ(HEARTH.y) + 1
        this.scene.addChild(this.flame)
        const entries = shelterEntries()
        entries.filter(entry => entry.layerId === "sprite").forEach(entry =>
            this.addTile(this.art, entry.tileSpec.tilesetId, atlasFrame(entry), entry.x, entry.y * CELL, rowZ(entryRow(entry))))
        entries.filter(entry => entry.layerId === "sky").forEach(entry =>
            this.addTile(this.roof, entry.tileSpec.tilesetId, atlasFrame(entry), entry.x, entry.y * CELL + this.lift, ROOF_Z))

        // Against the north wall, in front of the west one; seen from inside, so up to its hatch.
        for (const pixel of stackedLadderPixels({ kind: "bottom", height: Math.max(8, STOREY_RISE_PX), ceiling: true })) {
            this.ladder.beginFill(pixel.color).drawRect(pixel.x, pixel.y, pixel.width, pixel.height).endFill()
        }
        this.ladder.visible = false
        this.scene.addChild(this.ladder)

        this.walker.zIndex = bodyZ(START.y)
        this.scene.addChild(this.walker)
        this.walker.updatePaperdoll(this.body)

        this.target.zIndex = CURSOR_Z
        this.target.visible = false
        this.scene.addChild(this.target)
        this.feedback = new BuildPlacementFeedback(() => this.scene, () => this.clock)
        drawDioramaFrame(this.frame, SCENE_X, SCENE_Y, SCENE_W, SCENE_H)
        this.content.addChild(this.frame)

        // The ready bar where the HUD keeps it: docked in the bottom-left corner of the view.
        this.readyBar = new EquippedWidget({} as GuiManager)
        this.readyBar.position.set(SCENE_X + 6, SCENE_Y + SCENE_H - TRAY_HEIGHT - 6)
        this.content.addChild(this.readyBar)
        this.stops = this.cursorStops()

        this.fx.blendMode = BLEND_MODES.ADD
        this.content.addChild(this.fx)
        // In the free row over the shelter's north wall, clear of the ladder's top.
        this.stamp.place(SCENE_X + FIRE_X, SCENE_Y + (SHELTER_TOP * CELL + this.lift) / 2)
        this.content.addChild(this.stamp.burst, this.stamp.plaque)
        this.animate(0)
    }

    private addTile = (into: SceneArt[], sheet: string, tile: number, x: number, y: number, zIndex: number) => {
        const sprite = new Sprite(Texture.EMPTY)
        sprite.position.set(x * CELL, y)
        sprite.width = CELL; sprite.height = CELL
        sprite.zIndex = zIndex
        this.scene.addChild(sprite)
        into.push({ sprite, sheet, tile })
    }

    private buildFlame = (): FireSprite | FlameBackdrop => {
        try {
            const fire = new FireSprite()
            // FireSprite seats its coals 3px left of its origin; land them on the logs' crossing.
            fire.position.set(FIRE_X + 3, FIRE_BASE_Y - 4)
            return fire
        } catch {
            // If the real flame can't be built, the roster card's live one stands in.
            const flame = new FlameBackdrop(FLAME_W, FLAME_H, { glow: false })
            flame.position.set(FIRE_X - FLAME_W / 2, FIRE_BASE_Y - FLAME_H)
            return flame
        }
    }

    private dress = ({ sprite, sheet, tile }: SceneArt) => {
        const texture = contentSheetResource(sheet)?.textures?.[`${sheet}-${tile}`] || Texture.EMPTY
        if (sprite.texture !== texture) { sprite.texture = texture; sprite.width = CELL; sprite.height = CELL }
    }

    private cursorStops = (): LadderCursorStops => {
        const bar = this.readyBar
        return {
            rest: { x: SCENE_X + 2.4 * CELL, y: SCENE_Y + 2.6 * CELL },
            slot: { x: bar.x + TRAY_PAD + SLOT_PITCH + SLOT * 0.55, y: bar.y + TRAY_PAD + SLOT * 0.5 },
            enter: { x: SCENE_X + 9.1 * CELL, y: SCENE_Y + 2.7 * CELL },
            corner: { x: SCENE_X + (LADDER_CELL.x + 0.5) * CELL, y: SCENE_Y + (LADDER_CELL.y + 0.5) * CELL },
        }
    }

    protected rewind() {
        this.feedback.clear()
        this.played = { placed: false, built: false }
    }

    protected cursorAt(t: number) {
        return ladderCursorPose(t, this.stops)
    }

    protected captionAt(t: number) {
        const pose = ladderTutorialPose(t)
        return { text: pose.done ? DONE_CAPTION : caption(this.touch)[pose.step], done: pose.done }
    }

    protected poseScene(t: number, deltaMs: number) {
        const pose = ladderTutorialPose(t)
        if (this.selected !== pose.selected) {
            this.selected = pose.selected
            this.inventory.items.forEach((item, i) => item.active = i === pose.selected)
            this.readyBar.update(this.inventory, () => {})
        }
        this.readyBar.animate(deltaMs)

        this.art.forEach(this.dress)
        this.roof.forEach(art => { this.dress(art); art.sprite.alpha = pose.roofAlpha; art.sprite.visible = pose.roofAlpha > 0 })
        this.poseHearth(deltaMs)
        this.poseWalker(pose)
        this.poseLadder(t, pose)
        this.playFeedback(t, pose)
        this.drawFx(t)
        this.stamp.draw(t - STAMP_AT)
    }

    /** The real sprite driver reads position changes as well as the moving flag, so both are fed it. */
    private poseWalker = (pose: ReturnType<typeof ladderTutorialPose>) => {
        this.body.location = { x: pose.feet.x, y: pose.feet.y - FEET_BELOW_HIP }
        this.body.movement.angle = pose.facing
        this.body.movement.moving = pose.moving
        this.body.speed = 100
        this.body.speed2 = pose.moving ? pose.heading : { x: 0, y: 0 }
        this.walker.position.set(pose.feet.x, pose.feet.y - FEET_BELOW_HIP)
        this.walker.zIndex = bodyZ(pose.feet.y)
        this.walker.doMovement(this.body)
    }

    /**
     * Player.updateBuildGridMarker, for the pointer: with the ladder in hand the build cursor outlines the
     * pointer's cell, and the ladder's ghost stands in any cell of the interior's north row it is over.
     */
    private poseLadder = (t: number, pose: ReturnType<typeof ladderTutorialPose>) => {
        const pointer = this.cursorAt(t)
        const x = Math.floor((pointer.x - SCENE_X) / CELL), y = Math.floor((pointer.y - SCENE_Y) / CELL)
        const bar = this.readyBar
        const overBar = pointer.x < bar.x + bar.width + 4 && pointer.y > bar.y - 4
        const show = pose.selected === 1 && pointer.alpha > 0 && !overBar && x >= 0 && y >= 0 && x < COLS && y < ROWS
        this.target.visible = show && !pose.placed
        if (show) {
            this.target.moveTo(x * CELL, y * CELL)
            this.target.alpha = pointer.alpha
        }
        this.target.animate()

        const ghost = show && pose.inside && GHOST_CELLS.some(cell => cell.x === x && cell.y === y)
        const at = pose.placed ? LADDER_CELL : { x, y }
        this.ladder.visible = pose.placed || ghost
        // Down onto its feet as it lands, as BuildPlacementFeedback.settle drops a built tile.
        const drop = pose.placed ? 2 * (1 - easeOutCubic(span(t, BUILT_AT, BUILT_AT + 240))) : 0
        this.ladder.position.set(at.x * CELL, (at.y + 1) * CELL - drop)
        this.ladder.zIndex = rowZ(at.y) + 0.5
        this.ladder.alpha = pose.placed ? 1 : GHOST_ALPHA * pointer.alpha
    }

    /** Tell the game's own placement feedback about the click and the build, each once. */
    private playFeedback = (t: number, pose: ReturnType<typeof ladderTutorialPose>) => {
        const key = cellKey(LADDER_CELL)
        if (t >= PLACE_AT && !this.played.placed) {
            this.played.placed = true
            this.target.press()
            this.feedback.add(key, LADDER_CELL)
            this.feedback.setState(key, "valid")
        }
        if (pose.placed && !this.played.built) {
            this.played.built = true
            this.feedback.remove(key)
            this.feedback.spark(LADDER_CELL, "built")
        }
        this.feedback.update()
    }

    /** A burning fire, its light filling the shelter once the roof is cut away. */
    private poseHearth = (deltaMs: number) => {
        if (this.flame instanceof FireSprite) this.flame.setIntensity(0.6)
        else this.flame.update(deltaMs || 0, 0.6)
        const flicker = 0.5 + 0.5 * Math.sin(this.clock / 170) * Math.sin(this.clock / 310)
        glowDisc(this.hearthGlow.clear(), FIRE_X, FIRE_BASE_Y - 4, 44, FIRELIGHT, 0.16 + 0.05 * flicker, 5)
    }

    /** Ripples where the pointer pressed, and the ladder catching the light as it stands. */
    private drawFx = (t: number) => {
        const g = this.fx.clear()
        drawRipples(g, t, [SELECT_AT, PLACE_AT], at => ladderCursorPose(at, this.stops))
        if (t < BUILT_AT) return
        const ms = t - BUILT_AT
        const x = SCENE_X + (LADDER_CELL.x + 0.5) * CELL, y = SCENE_Y + (LADDER_CELL.y + 1) * CELL - 30
        const bloom = 1 - easeOutCubic(span(ms, 0, 700))
        if (bloom > 0) glowDisc(g, x, y, 34, REWARD_GOLD_HOT, 0.45 * bloom, 5)
        sparkBurst(g, x, y, span(ms, 0, 650), 12, 10, 34, REWARD_GOLD, 0.6, 1.6)
    }

    protected teardown() {
        // First: the feedback destroys its own graphics, which the children sweep would otherwise
        // destroy a second time.
        this.feedback.destroy()
        this.walker.cleanup()
        // The real flame plays on the shared ticker; its baked frames are shared and stay.
        if (this.flame instanceof FireSprite) this.flame.cleanup()
    }
}
